import { API_BASE } from '../config/apiBase';

export { API_BASE };

/** Same rules as backend publicAssetUrl — avoid mixed content on HTTPS storefront. */
function isPrivateOrLocalHostname(hostname) {
  if (!hostname) return false;
  if (hostname === 'localhost') return true;
  if (hostname === '127.0.0.1') return true;
  const m = /^(\d+)\.(\d+)\.\d+\.\d+$/.exec(hostname);
  if (!m) return false;
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return false;
}

/**
 * Join API_BASE with a path like `/api/admin/...`.
 * If REACT_APP_API_URL mistakenly ends with `/api`, avoid `.../api/api/admin/...` (404).
 */
export function joinApiUrl(path) {
  if (path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  const base = (API_BASE || '').replace(/\/+$/, '');
  if (!base) return p;
  if (base.endsWith('/api') && (p.startsWith('/api') || p.startsWith('/uploads'))) {
    return base.replace(/\/api$/i, '') + p;
  }
  return base + p;
}

/**
 * Storefront reads (products, categories, hero, filters): In local dev when REACT_APP_API_URL
 * points at this machine (`localhost` / `127.0.0.1`), use same-origin `/api/...` so Create React App’s
 * `package.json` `proxy` forwards to the backend. Absolute `http://localhost:4000/api/...` bypasses
 * the proxy and can hang or fail in some browsers / mixed-network setups.
 */
export function joinPublicStorefrontUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  const raw = process.env.REACT_APP_API_URL;
  const trimmed = raw != null ? String(raw).trim() : '';
  const devLocalApi =
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
    (trimmed === '' || /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(trimmed));
  if (devLocalApi) return p;
  return joinApiUrl(p);
}

/** Resolve image URL for display: relative paths (e.g. /uploads/...) become API_BASE + path so images load from the backend. */
export function getImageUrl(url) {
  if (url == null || typeof url !== 'string' || !url.trim()) return '';
  const s = url.trim();
  if (s.startsWith('https://')) return s;
  if (s.startsWith('http://')) {
    try {
      const parsed = new URL(s);
      if (isPrivateOrLocalHostname(parsed.hostname)) {
        return joinApiUrl(`${parsed.pathname}${parsed.search}`);
      }
      const base = (API_BASE || '').replace(/\/+$/, '');
      if (base && typeof window !== 'undefined' && window.location.protocol === 'https:') {
        const baseUrl = new URL(base.startsWith('http') ? base : `https://${base}`);
        if (parsed.hostname === baseUrl.hostname) {
          return `https://${parsed.host}${parsed.pathname}${parsed.search}`;
        }
      }
    } catch (_) {}
    return s;
  }
  return joinApiUrl(s);
}

export function getToken() {
  return localStorage.getItem('admin_token');
}

export function setToken(token) {
  localStorage.setItem('admin_token', token);
}

export function clearToken() {
  localStorage.removeItem('admin_token');
}

// Customer token (storefront; separate from admin_token)
const CUSTOMER_TOKEN_KEY = 'customer_token';
export const CUSTOMER_AUTH_EVENT = 'customer-auth-changed';

function notifyCustomerAuthChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CUSTOMER_AUTH_EVENT));
}

export function getCustomerToken() {
  return localStorage.getItem(CUSTOMER_TOKEN_KEY);
}

export function setCustomerToken(token) {
  localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
  notifyCustomerAuthChanged();
}

export function clearCustomerToken() {
  localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  notifyCustomerAuthChanged();
}

/** Customer API: sends Authorization Bearer customer_token. Use for /api/customer/* and for placing orders as customer. */
export async function customerApi(path, options = {}) {
  const url = joinApiUrl(path);
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const token = getCustomerToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(url, { ...options, headers });
  const data = res.ok ? await res.json().catch(() => ({})) : null;
  if (!res.ok) throw new Error(data && data.error ? data.error : res.statusText || 'Request failed');
  return data;
}

export async function api(path, options = {}) {
  const url = joinApiUrl(path);
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const token = getToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(url, { ...options, headers });
  const data = res.ok ? await res.json().catch(() => ({})) : null;
  if (!res.ok) throw new Error(data && data.error ? data.error : res.statusText || 'Request failed');
  return data;
}

/** Admin download (CSV, etc.): sends Bearer token, returns Blob. Caller triggers file save. */
export async function adminDownloadBlob(path) {
  const url = joinApiUrl(path);
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(url, { headers });
  const ct = res.headers.get('Content-Type') || '';
  if (!res.ok) {
    const err = ct.includes('application/json') ? await res.json().catch(() => ({})) : {};
    throw new Error(err.error || res.statusText || 'Download failed');
  }
  return res.blob();
}

/** Upload a file to admin upload endpoint; returns relative URL (e.g. /uploads/file.jpg).
 *  Always store this relative path in the DB so it stays portable across environments.
 *  Use getImageUrl() when rendering — it prepends the correct API base at display time. */
export async function uploadImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(joinApiUrl('/api/admin/upload'), {
    method: 'POST',
    headers: token ? { Authorization: 'Bearer ' + token } : {},
    body: formData,
  });
  const data = res.ok ? await res.json().catch(() => ({})) : null;
  if (!res.ok) throw new Error(data && data.error ? data.error : res.statusText || 'Upload failed');
  // Return the raw relative path so it's portable (avoids saving localhost URLs in DB).
  return data.url || '';
}
